/**
 * AIPanelContext — global state for the AI assistant panel.
 * Keeps shell collapse state, panel triggers, and pinned screen items aligned.
 */
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";
import type { AssistantPanelContext, AssistantSelectedItem } from "@/services/modules/assistant";

export type AIPanelOpenRequest = {
  prompt?: string;
  mode?: "chat" | "copilot";
  panelContext?: AssistantPanelContext;
};

type AIPanelState = {
  open: boolean;
  collapsed: boolean;
  pendingRequest: AIPanelOpenRequest | null;
};

type AIPanelContextValue = {
  panelState: AIPanelState;
  collapsed: boolean;
  pinnedItems: AssistantSelectedItem[];
  openPanel: (request?: string | AIPanelOpenRequest) => void;
  closePanel: () => void;
  toggleCollapsed: () => void;
  consumePendingRequest: () => AIPanelOpenRequest | null;
  pinItem: (item: AssistantSelectedItem) => void;
  pinItems: (items: AssistantSelectedItem[]) => void;
  unpinItem: (id: string) => void;
  clearPinnedItems: () => void;
  openPanelWithItems: (items: AssistantSelectedItem[], prompt?: string, mode?: "chat" | "copilot") => void;
};

const AIPanelContext = createContext<AIPanelContextValue | null>(null);
const PANEL_COLLAPSED_STORAGE_KEY = "nipsys:ai-panel:collapsed";
const PINNED_ITEMS_STORAGE_KEY = "nipsys:ai-panel:pinned-items";

function readInitialCollapsedState() {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(PANEL_COLLAPSED_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
}

function persistCollapsedState(collapsed: boolean) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(PANEL_COLLAPSED_STORAGE_KEY, String(collapsed));
  } catch {}
}

function readInitialPinnedItems(): AssistantSelectedItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.sessionStorage.getItem(PINNED_ITEMS_STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function persistPinnedItems(items: AssistantSelectedItem[]) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(PINNED_ITEMS_STORAGE_KEY, JSON.stringify(items));
  } catch {}
}

export function AIPanelProvider({ children }: { children: ReactNode }) {
  const [panelState, setPanelState] = useState<AIPanelState>(() => {
    const collapsed = readInitialCollapsedState();
    return {
      open: !collapsed,
      collapsed,
      pendingRequest: null,
    };
  });

  const [pinnedItems, setPinnedItems] = useState<AssistantSelectedItem[]>(() => readInitialPinnedItems());

  useEffect(() => {
    persistPinnedItems(pinnedItems);
  }, [pinnedItems]);

  const pinItem = useCallback((item: AssistantSelectedItem) => {
    setPinnedItems((prev) => {
      if (prev.some((p) => p.id === item.id)) return prev;
      return [...prev, item];
    });
  }, []);

  const pinItems = useCallback((items: AssistantSelectedItem[]) => {
    setPinnedItems((prev) => {
      const existingIds = new Set(prev.map((p) => p.id));
      const newItems = items.filter((item) => !existingIds.has(item.id));
      return [...prev, ...newItems];
    });
  }, []);

  const unpinItem = useCallback((id: string) => {
    setPinnedItems((prev) => prev.filter((p) => p.id !== id));
  }, []);

  const clearPinnedItems = useCallback(() => {
    setPinnedItems([]);
  }, []);

  const openPanel = useCallback((request?: string | AIPanelOpenRequest) => {
    const normalizedRequest =
      typeof request === "string"
        ? { prompt: request, mode: "chat" as const }
        : request ?? null;

    persistCollapsedState(false);
    setPanelState((current) => ({
      open: true,
      collapsed: false,
      pendingRequest: normalizedRequest ?? current.pendingRequest,
    }));
  }, []);

  const openPanelWithItems = useCallback(
    (items: AssistantSelectedItem[], prompt?: string, mode: "chat" | "copilot" = "copilot") => {
      pinItems(items);
      const defaultPrompt =
        prompt ||
        `Analise os ${items.length} itens selecionados (${items.map((i) => i.code || i.title).join(", ")}) e aponte situação jurídica, riscos imediatos e recomendações operacionais.`;

      openPanel({
        mode,
        prompt: defaultPrompt,
        panelContext: {
          screen: "Itens Selecionados",
          entityType: items.length === 1 ? items[0].type : "selecao_multipla",
          entityId: items.length === 1 ? items[0].id : undefined,
          intentLabel: `Analisar ${items.length} item(ns)`,
          selectedItems: items,
        },
      });
    },
    [openPanel, pinItems]
  );

  const closePanel = useCallback(() => {
    persistCollapsedState(true);
    setPanelState((current) => ({
      ...current,
      open: false,
      collapsed: true,
    }));
  }, []);

  const toggleCollapsed = useCallback(() => {
    setPanelState((current) => {
      const nextCollapsed = !current.collapsed;
      persistCollapsedState(nextCollapsed);
      return {
        ...current,
        collapsed: nextCollapsed,
        open: !nextCollapsed,
      };
    });
  }, []);

  const consumePendingRequest = useCallback(() => {
    const pending = panelState.pendingRequest;
    if (!pending) return null;
    setPanelState((current) => ({
      ...current,
      pendingRequest: null,
    }));
    return pending;
  }, [panelState.pendingRequest]);

  return (
    <AIPanelContext.Provider
      value={{
        panelState,
        collapsed: panelState.collapsed,
        pinnedItems,
        openPanel,
        closePanel,
        toggleCollapsed,
        consumePendingRequest,
        pinItem,
        pinItems,
        unpinItem,
        clearPinnedItems,
        openPanelWithItems,
      }}
    >
      {children}
    </AIPanelContext.Provider>
  );
}

export function useAIPanel() {
  const context = useContext(AIPanelContext);
  if (!context) {
    throw new Error("useAIPanel must be used within an AIPanelProvider");
  }
  return context;
}
